import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, ShieldCheck, X, Check } from "lucide-react";
import api from "../lib/api";
import { IS_SC_PREVIEW_FRAME } from "../lib/siteContent";

const LS_KEY = "plannio_cookie_consent";
const LS_ANON = "plannio_anon_id";

function getAnonId() {
  let id = localStorage.getItem(LS_ANON);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(LS_ANON, id);
  }
  return id;
}

function readStored() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; }
}

export default function CookieBanner() {
  const location = useLocation();
  if (IS_SC_PREVIEW_FRAME || location.pathname.startsWith("/admin")) return null;
  return <CookieBannerInner />;
}

function CookieBannerInner() {
  const [config, setConfig] = useState(null);
  const [version, setVersion] = useState(null);
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [selection, setSelection] = useState({});
  const [reconsent, setReconsent] = useState(false);
  const location = useLocation();
  const compact = location.pathname.startsWith("/privacy/requests") || location.pathname.startsWith("/privacy/status");

  const init = useCallback(async () => {
    let cfg;
    try { cfg = (await api.get("/consents/cookie-config")).data; }
    catch { return; }
    setConfig(cfg);
    setVersion(cfg.cookie_version);
    const base = {};
    (cfg.categories || []).forEach((c) => { base[c.id] = !!c.required; });
    const stored = readStored();
    if (stored?.categories) Object.assign(base, stored.categories);
    base.necessary = true;
    setSelection(base);
    // Re-prompt when there's no local choice OR the policy version has changed (re-consent).
    const stale = !stored || String(stored.version) !== String(cfg.cookie_version);
    let needs = stale;
    if (stored && String(stored.version) !== String(cfg.cookie_version)) setReconsent(true);
    // Logged-in users: only escalate a re-prompt when the SERVER reports a genuine version bump.
    // A valid local choice at the current version is authoritative for the session, so we never
    // re-show the banner just because the server record has not caught up (avoids it reappearing
    // on every route after the user has already accepted).
    if (!needs && localStorage.getItem("plannio_token")) {
      try {
        const cur = (await api.get("/consents/cookie/current")).data;
        if (cur.stale) { needs = true; setReconsent(true); }
      } catch { /* ignore */ }
    }
    setVisible(needs);
  }, []);

  useEffect(() => {
    init();
    const openHandler = () => { setManaging(true); setVisible(true); };
    window.addEventListener("open-cookie-preferences", openHandler);
    return () => window.removeEventListener("open-cookie-preferences", openHandler);
  }, [init]);

  const persist = (categories) => {
    localStorage.setItem(LS_KEY, JSON.stringify({ categories, version, savedAt: new Date().toISOString() }));
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: { categories } }));
  };

  const submit = async (method, categories) => {
    persist(categories);
    setVisible(false);
    setManaging(false);
    try {
      await api.post("/consents/cookie", { method, categories, anonymous_id: getAnonId(), source: window.location.pathname });
    } catch { /* stored locally regardless */ }
  };

  const acceptAll = () => {
    const cats = {}; (config?.categories || []).forEach((c) => { cats[c.id] = true; });
    submit("accept_all", cats);
  };
  const necessaryOnly = () => {
    const cats = {}; (config?.categories || []).forEach((c) => { cats[c.id] = !!c.required; });
    submit("necessary_only", cats);
  };
  const saveCustom = () => submit("custom", { ...selection, necessary: true });

  // While the banner is visible, reserve space at the bottom of the page so the fixed
  // banner card never visually overlaps page content / action bars. We pad document.body
  // and also expose a --cookie-banner-h CSS variable for inner scroll containers (e.g. admin).
  const wrapRef = useRef(null);
  useEffect(() => {
    const clear = () => { document.body.style.paddingBottom = ""; document.documentElement.style.setProperty("--cookie-banner-h", "0px"); };
    if (!visible || !config) { clear(); return; }
    const apply = () => {
      const h = wrapRef.current?.offsetHeight || 0;
      const v = h ? `${h + 8}px` : "0px";
      document.body.style.paddingBottom = h ? v : "";
      document.documentElement.style.setProperty("--cookie-banner-h", v);
    };
    apply();
    const ro = window.ResizeObserver ? new ResizeObserver(apply) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", apply);
    return () => { ro?.disconnect(); window.removeEventListener("resize", apply); clear(); };
  }, [visible, config, managing, reconsent]);

  if (!visible || !config) return null;

  // Low-profile variant on focused task pages (e.g. the Privacy Centre form) so the
  // primary submission controls stay visible.
  if (compact && !managing) {
    return (
      <div ref={wrapRef} className="pointer-events-none fixed bottom-0 right-0 z-[80] w-full max-w-sm px-3 pb-3" data-testid="cookie-banner">
        <div className="pointer-events-auto rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-xl dark:border-white/10 dark:bg-[#1b2422]">
          <p className="text-xs leading-relaxed text-[#636E72] dark:text-gray-300">
            <Cookie className="mr-1 inline h-3.5 w-3.5 text-[#0F766E]" aria-hidden="true" />
            We use cookies to keep PlannioLife secure and remember your preferences. Strictly necessary cookies are always on.
            {" "}<a href="/cookies" className="font-medium text-[#0F766E] hover:underline">Cookie Policy</a>
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <button data-testid="cookie-accept-all-btn" onClick={acceptAll} className="rounded-lg bg-[#0F766E] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0d655e]">Accept all</button>
            <button data-testid="cookie-necessary-btn" onClick={necessaryOnly} className="rounded-lg border border-[#0F766E]/30 px-3 py-1.5 text-xs font-medium text-[#0F766E] hover:bg-[#0F766E]/5">Necessary only</button>
            <button data-testid="cookie-manage-btn" onClick={() => setManaging(true)} className="rounded-lg px-3 py-1.5 text-xs font-medium text-[#636E72] hover:bg-[#f0f1ee] dark:text-gray-300 dark:hover:bg-white/5">Manage</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] px-3 pb-3 sm:px-5 sm:pb-5" data-testid="cookie-banner">
      <div className="pointer-events-auto relative mx-auto max-w-3xl overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-2xl dark:border-white/10 dark:bg-[#1b2422]">
        {!managing ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0F766E]/10 text-[#0F766E]"><Cookie className="h-5 w-5" strokeWidth={1.7} /></span>
              <div className="min-w-0">
                <h3 className="font-display text-base font-semibold text-[#2D3436] dark:text-white" data-testid="cookie-banner-title">{reconsent ? "We've updated our Cookie Policy" : "We value your privacy"}</h3>
                <p className="mt-1 text-sm leading-relaxed text-[#636E72] dark:text-gray-300">
                  {reconsent
                    ? "Our Cookie Policy has changed. Please review your choices again to continue. "
                    : "We use cookies to help keep PlannioLife running securely, remember your preferences and understand how the service is used. "}
                  Strictly necessary cookies are always on. You can accept all, keep only necessary, or manage your choices.
                  Read our <a href="/cookies" className="font-medium text-[#0F766E] hover:underline">Cookie Policy</a>.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button data-testid="cookie-manage-btn" onClick={() => setManaging(true)}
                className="rounded-xl border border-[#E2E8F0] px-4 py-2.5 text-sm font-medium text-[#636E72] hover:bg-[#f0f1ee] dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5">Manage preferences</button>
              <button data-testid="cookie-necessary-btn" onClick={necessaryOnly}
                className="rounded-xl border border-[#0F766E]/30 px-4 py-2.5 text-sm font-medium text-[#0F766E] hover:bg-[#0F766E]/5">Necessary only</button>
              <button data-testid="cookie-accept-all-btn" onClick={acceptAll}
                className="rounded-xl bg-[#0F766E] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#0d655e]">Accept all</button>
            </div>
          </div>
        ) : (
          <div className="max-h-[80vh] overflow-y-auto p-5 sm:p-6">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-[#2D3436] dark:text-white"><ShieldCheck className="h-5 w-5 text-[#0F766E]" strokeWidth={1.7} /> Manage cookie preferences</h3>
              <button data-testid="cookie-close-btn" onClick={() => setManaging(false)} className="rounded-lg p-1.5 text-[#9aa3a0] hover:bg-[#f0f1ee] dark:hover:bg-white/5"><X className="h-4 w-4" /></button>
            </div>
            <div className="divide-y divide-[#F1F3EF] dark:divide-white/10">
              {(config.categories || []).map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-3 py-3.5" data-testid={`cookie-cat-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-[#2D3436] dark:text-gray-100">{c.label}</span>
                      {c.required && <span className="rounded-full bg-[#0F766E]/10 px-2 py-0.5 text-[10px] font-semibold text-[#0F766E]">Always on</span>}
                    </div>
                    <p className="mt-0.5 break-words text-xs leading-relaxed text-[#636E72] dark:text-gray-400">{c.description}</p>
                  </div>
                  <button
                    type="button"
                    data-testid={`cookie-toggle-${c.id}`}
                    disabled={c.required}
                    onClick={() => setSelection((s) => ({ ...s, [c.id]: !s[c.id] }))}
                    className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${(c.required || selection[c.id]) ? "bg-[#0F766E]" : "bg-[#CBD5E1] dark:bg-white/20"} ${c.required ? "cursor-not-allowed opacity-70" : ""}`}
                    aria-checked={c.required || !!selection[c.id]}
                    role="switch"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${(c.required || selection[c.id]) ? "translate-x-[1.375rem]" : "translate-x-0.5"}`} />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button data-testid="cookie-reject-all-btn" onClick={necessaryOnly}
                className="rounded-xl border border-[#E2E8F0] px-4 py-2.5 text-sm font-medium text-[#636E72] hover:bg-[#f0f1ee] dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5">Reject all</button>
              <button data-testid="cookie-save-btn" onClick={saveCustom}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-[#0F766E] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#0d655e]"><Check className="h-4 w-4" /> Save preferences</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
